import {
  FiExternalLink,
  FiFile,
  FiFileText,
  FiImage,
  FiTrash2,
  FiUpload,
  FiVideo,
} from "react-icons/fi";
import PropTypes from "prop-types";
import { isFileObject } from "utils/uploadFormItems";

function UploadSpinner() {
  return (
    <span
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
      aria-hidden
    >
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-brand-900" />
    </span>
  );
}

function getSafeFileName(input, fallbackName) {
  if (!input) {
    const fb = String(fallbackName ?? "").trim();
    return fb || "file";
  }
  if (typeof input === "string") {
    const withoutQuery = input.split("?")[0];
    const parts = withoutQuery.split("/");
    return parts.at(-1) || "file";
  }

  if (input.name) return input.name;
  if (input.url) return getSafeFileName(input.url);
  return "file";
}

function getFileKind(fileLike, displayName) {
  const fileName = getSafeFileName(fileLike, displayName);
  const extension = fileName.split(".").pop()?.toLowerCase() || "";
  const type = typeof fileLike === "object" ? fileLike?.type || "" : "";

  if (type.includes("pdf") || extension === "pdf") {
    return { label: "PDF", Icon: FiFileText };
  }

  if (
    type.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp"].includes(extension)
  ) {
    return { label: extension.toUpperCase() || "IMAGE", Icon: FiImage };
  }

  if (
    type.startsWith("video/") ||
    ["mp4", "mov", "avi", "mkv", "webm", "m4v"].includes(extension)
  ) {
    return { label: extension.toUpperCase() || "VIDEO", Icon: FiVideo };
  }

  return { label: extension.toUpperCase() || "FILE", Icon: FiFile };
}

function getItemFileLike(item) {
  if (!item) return null;
  if (typeof item.file === "string") return item.file;
  if (item.file) return item.file;
  if (item.url) return item.url;
  return null;
}

function getItemIdentifier(item, index) {
  return item?.id || item?.url || item?.file?.name || `upload-item-${index}`;
}

function uploadStatusLabel(status) {
  if (status === "complete") return "Complete";
  if (status === "uploading") return "Uploading";
  return "Ready";
}

export default function FileUploadPreviewList({
  items,
  showActions = true,
  showProgress = true,
  onUpload,
  onOpen,
  onDelete,
}) {
  if (!items?.length) return null;

  return (
    <div className="mt-4 space-y-3">
      {items.map((item, index) => {
        const fileLike = getItemFileLike(item);
        const displayName = item?.name;
        const fileName = getSafeFileName(fileLike, displayName);
        const itemId = getItemIdentifier(item, index);
        const { label, Icon } = getFileKind(fileLike, displayName);

        const hasLocalFileToUpload = isFileObject(item?.file);
        const isUploading = item?.status === "uploading";

        const showUploadTrigger = onUpload && hasLocalFileToUpload && !isUploading;
        const showUploadProgressControl = onUpload && hasLocalFileToUpload && isUploading;

        return (
          <div key={itemId} className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-text-700">
                  <Icon />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-text-800">{fileName}</p>
                  <p className="text-xs text-text-500">{label}</p>
                </div>
              </div>

              {showActions && (
                <div className="flex items-center gap-1">
                  {showUploadProgressControl ? (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-medium text-text-600"
                      title="Uploading…"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <UploadSpinner />
                      <span className="sr-only">Uploading file</span>
                    </span>
                  ) : null}
                  {showUploadTrigger ? (
                    <button
                      type="button"
                      title="Upload"
                      onClick={() => onUpload(itemId, item)}
                      className="rounded-md border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
                    >
                      <FiUpload size={14} />
                    </button>
                  ) : null}
                  {onOpen ? (
                    <button
                      type="button"
                      title="Open"
                      onClick={() => onOpen(item)}
                      className="rounded-md border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
                    >
                      <FiExternalLink size={14} />
                    </button>
                  ) : null}
                  {onDelete ? (
                    <button
                      type="button"
                      title="Delete"
                      onClick={() => onDelete(itemId, item)}
                      className="rounded-md border border-slate-200 p-2 text-danger transition hover:bg-red-50"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            {showProgress && (
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-text-500">{uploadStatusLabel(item.status)}</span>
                  <span className="font-semibold text-text-700">{item.progress || 0}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-success transition-all"
                    style={{ width: `${item.progress || 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

FileUploadPreviewList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      file: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
        }),
      ]),
      url: PropTypes.string,
      status: PropTypes.string,
      progress: PropTypes.number,
    })
  ),
  showActions: PropTypes.bool,
  showProgress: PropTypes.bool,
  onUpload: PropTypes.func,
  onOpen: PropTypes.func,
  onDelete: PropTypes.func,
};
